<template>
  <div class="metrics-chart-card" data-testid="metrics-ai-detection-card">
    <div class="metrics-chart-card__header">
      <span class="metrics-chart-card__title heading-s">
        {{ $t(`metrics.${chartTitleKey}`) }}
        <eg-icon
          icon="information"
          class="metrics-chart-card__info-icon"
          @click.native="
            $eventBus.$emit(
              selectedType === 'usedAI'
                ? 'onboarding:clicked:metricsAiDetectionHint'
                : 'onboarding:clicked:metricsPlagiarismHint'
            )
          "
        />
      </span>

      <div class="metrics-chart-card__filters">
        <eg-dropdown
          ref="typeDropdown"
          :text="selectedTypeLabel"
          class="metrics-chart-card__filter-dropdown"
        >
          <eg-b-dropdown-item-button
            v-for="option in typeOptions"
            :key="option.value"
            @click="$emit('update:selectedType', option.value)"
          >
            {{ option.label }}
          </eg-b-dropdown-item-button>
        </eg-dropdown>
        <eg-dropdown
          ref="metricDropdown"
          :text="selectedMetricLabel"
          class="metrics-chart-card__filter-dropdown"
        >
          <eg-b-dropdown-item-button
            v-for="option in metricOptions"
            :key="option.value"
            @click="$emit('update:selectedMetric', option.value)"
          >
            {{ option.label }}
          </eg-b-dropdown-item-button>
        </eg-dropdown>
      </div>
    </div>

    <div class="metrics-chart-card__body">
      <infinity-loading v-if="isLoading && !hasData" />
      <chart-j-s-bar
        v-else-if="hasData"
        :chart-data="chartData"
        :chart-options="chartOptions"
        width="100%"
        :height="200"
      />
      <metrics-empty-state v-else />
    </div>
  </div>
</template>

<script>
import ChartJSBar from "@/components/global/chartjs/chart_js_bar.vue";
import MetricsEmptyState from "../shared/metrics_empty_state.vue";
import InfinityLoading from "@/components/rubrics/newRubric/components/infinity-loading.vue";
import dropdownHideOnLeaveMixin from "@/mixins/dropdownHideOnLeaveMixin";

export default {
  name: "MetricsAiDetectionChart",

  components: {
    ChartJSBar,
    MetricsEmptyState,
    InfinityLoading,
  },

  mixins: [dropdownHideOnLeaveMixin],

  props: {
    data: {
      type: Object,
      required: true,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    selectedType: {
      type: String,
      default: "usedAI",
    },
    selectedMetric: {
      type: String,
      default: "average",
    },
  },

  computed: {
    typeOptions() {
      return [
        { value: "plagiarism", label: this.$t("metrics.flagsTypePlagiarism") },
        { value: "usedAI", label: this.$t("metrics.flagsTypeAiDetection") },
      ];
    },

    metricOptions() {
      return [
        { value: "average", label: this.$t("metrics.flagsMetricAverage") },
        { value: "count", label: this.$t("metrics.flagsMetricCount") },
      ];
    },

    isAverageMetric() {
      return this.selectedMetric === "average";
    },

    chartTitleKey() {
      if (this.selectedType === "usedAI") {
        return this.selectedMetric === "average"
          ? "aiDetectionAverage"
          : "aiDetectionCount";
      }
      return this.selectedMetric === "average"
        ? "plagiarismAverage"
        : "plagiarismCount";
    },

    selectedTypeLabel() {
      return (
        this.typeOptions.find((o) => o.value === this.selectedType)?.label ?? ""
      );
    },

    selectedMetricLabel() {
      return (
        this.metricOptions.find((o) => o.value === this.selectedMetric)
          ?.label ?? ""
      );
    },

    hasData() {
      if (!this.data.labels?.length) return false;
      return (
        this.data.datasets?.some((ds) =>
          ds.data?.some((v) => v != null && v !== 0)
        ) ?? false
      );
    },

    chartData() {
      return {
        labels: this.data.labels,
        datasets: [
          {
            data: this.data.datasets?.[0].data,
            backgroundColor: "#C2714F",
            borderRadius: 4,
            borderSkipped: false,
          },
        ],
      };
    },

    countYMax() {
      const values = this.data.datasets?.[0]?.data ?? [];
      const max = Math.max(0, ...values.filter((v) => v != null));
      return Math.ceil((max + 1) / 5) * 5;
    },

    chartOptions() {
      const isAverage = this.isAverageMetric;
      const tickFont = {
        color: "#566370",
        font: {
          family: "Inter",
          size: 12,
          weight: "400",
          lineHeight: 1.2,
        },
      };
      return {
        responsive: true,
        maintainAspectRatio: false,
        onHover: (event, activeElements) => {
          event.native.target.style.cursor =
            activeElements.length > 0 ? "pointer" : "default";
        },
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: (ctx) =>
                isAverage ? `${ctx.parsed.y}%` : `${ctx.parsed.y}`,
            },
          },
        },
        scales: {
          x: {
            grid: { display: false },
            border: { display: false },
            ticks: tickFont,
          },
          y: {
            grid: { color: "#F0F3F6" },
            border: { display: false },
            min: 0,
            ...(isAverage ? { max: 100 } : { max: this.countYMax }),
            ticks: {
              ...tickFont,
              ...(isAverage
                ? { callback: (v) => `${v}%`, stepSize: 10 }
                : { precision: 0 }),
            },
          },
        },
      };
    },
  },

  methods: {
    hideDropdowns() {
      this.$refs.typeDropdown?.hide();
      this.$refs.metricDropdown?.hide();
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../shared/metrics_chart_card";

.metrics-chart-card {
  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 16px;
  }

  &__filters {
    display: flex;
    gap: 8px;
  }

  &__filter-dropdown {
    @include metrics-dropdown;
    ::v-deep .btn {
      padding: 4px 10px;
    }
  }

  &__body {
    min-height: 200px;
  }
}
</style>
